import { Container, Graphics, Text } from "pixi.js"
import TextInput from "pixi-text-input"
import { TextRenderer } from "../text/text_renderer"
import { COLORS, CONTENT_W, FieldTone, INTRO_FONT, METRICS, drawFieldBox } from "./intro_theme"

interface LoginFieldProps {
    label: string
    placeholder?: string
    /** Maps to the DOM input's `autocomplete`, which is what lets a password manager fill and save. */
    autocomplete: "username" | "current-password"
    password?: boolean
    onInput?: (value: string) => void
    onEnter?: () => void
    onEscape?: () => void
}

/** How tall one labelled field is, label included -- callers stack these without measuring. */
export const FIELD_BLOCK_H = 16 + 8 + METRICS.fieldHeight

/**
 * A labelled text field: our own frame drawn in pixi, with pixi-text-input's DOM `<input>` sitting
 * over it.
 *
 * Two departures from how the intro used this library before:
 *
 *  - `substituteText = false`. By default the library hides the real input and paints a pixi Text
 *    copy whenever the field is unfocused. That copy is a canvas raster scaled by the stage
 *    transform (~1.79x here), and a hidden input is invisible to password managers. Leaving the
 *    real input in place gives crisp text at any zoom and lets 1Password/Chrome see the field.
 *  - `box` is a function rather than a style object, so the frame is ours: the library calls it
 *    with the measured input size and the DEFAULT/FOCUSED state, and we add an `error` tone on top.
 */
export class LoginField extends Container {
    readonly input: any
    private labelText: Text
    private tone: FieldTone = "default"

    constructor({ label, placeholder, autocomplete, password, onInput, onEnter, onEscape }: LoginFieldProps) {
        super()

        this.labelText = new TextRenderer()
            .fontFamily(INTRO_FONT)
            .fontSize(12)
            .color(COLORS.muted)
            .render(label.toUpperCase()) as Text
        this.labelText.style.fontWeight = "500"
        this.labelText.style.letterSpacing = 1.1
        this.addChild(this.labelText)

        this.input = new TextInput({
            input: {
                fontFamily: INTRO_FONT,
                fontSize: "16px",
                fontWeight: "400",
                lineHeight: `${METRICS.fieldHeight}px`,
                height: `${METRICS.fieldHeight}px`,
                width: `${CONTENT_W}px`,
                boxSizing: "border-box",
                padding: "0 14px",
                color: "#f2f4ef",
                caretColor: "#8fd66b",
                multiline: false,
            },
            box: (w: number, h: number, state: string) => {
                const g = new Graphics()
                drawFieldBox(g, w, h, this.tone === "error" ? "error" : state === "FOCUSED" ? "focused" : "default")
                return g
            },
        })

        // Keep the real <input> on screen instead of the library's pixi Text stand-in -- see the
        // class comment.
        //
        // The constructor always builds the stand-in before we can opt out, and the library's
        // _destroySurrogate() drops the Text and its hitbox but NOT _surrogate_mask -- a Graphics
        // filled with beginFill(0), i.e. opaque black, covering the field inset by its padding.
        // Left behind it paints a black slab over our frame. Take it out here.
        this.input.substituteText = false
        const orphanedMask: Graphics | undefined = this.input._surrogate_mask
        if (orphanedMask) {
            this.input.removeChild(orphanedMask)
            orphanedMask.destroy()
            this.input._surrogate_mask = null
        }

        this.input.y = 24
        this.addChild(this.input)

        const dom: HTMLInputElement = this.input._dom_input
        dom.className = "samsara-input"
        dom.setAttribute("autocomplete", autocomplete)
        dom.setAttribute("name", autocomplete === "current-password" ? "password" : "username")
        dom.setAttribute("aria-label", label)
        if (password) {
            dom.type = "password"
        } else {
            dom.setAttribute("autocapitalize", "off")
            dom.setAttribute("autocorrect", "off")
            dom.setAttribute("spellcheck", "false")
            this.input.maxLength = 24
        }

        if (placeholder) {
            this.input.placeholder = placeholder
        }

        if (onInput) {
            this.input.on("input", onInput)
        }
        this.input.on("keyup", (keycode: number) => {
            if (keycode === 13 && onEnter) {
                onEnter()
            }
            if (keycode === 27 && onEscape) {
                onEscape()
            }
        })

        // Typing in a field that is showing an error clears the error rim -- the message itself is
        // cleared by the card, which owns it.
        this.input.on("input", () => {
            if (this.tone === "error") {
                this.setTone("default")
            }
        })
    }

    get value(): string {
        return (this.input.text || "").trim()
    }

    set value(next: string) {
        this.input.text = next
    }

    /**
     * Deferred by a tick on purpose: a focus() issued while the browser is still dispatching the
     * pointerdown/click that triggered it gets handed straight back to the canvas, so switching to
     * the sign-in fields by clicking "Log in" would leave you typing at the game instead.
     */
    focus = () => {
        setTimeout(() => this.input.focus(), 0)
    }

    setTone = (tone: FieldTone) => {
        if (this.tone === tone) {
            return
        }
        this.tone = tone
        // Force the library to re-run our box generator: it caches one box per state and only
        // rebuilds when the input's measured size changes, which a tone change does not.
        this.input._buildBoxCache()
        this.input._setState(this.input.state)
    }

    /**
     * Follow the field on and off screen.
     *
     * Hiding has to be done here and now. pixi-text-input syncs the real `<input>`'s css `display`
     * from inside `render()`, so the frame an ancestor's `visible` goes false is the last chance the
     * library gets -- pixi stops drawing the subtree, and the input (placeholder and all) stays
     * painted over whatever comes next. Cf. `gui/name_prompt.ts`, which hit this first.
     *
     * Showing is handed back to the library on purpose: it places the node and sets `display` in
     * the same pass, whereas an input it has not placed yet still sits at the page's top-left, so
     * revealing one by hand flashes the field in the corner. All it needs is a reason to re-sync --
     * it skips the work when nothing it watches has changed, and our hide was invisible to it.
     */
    setDomVisible = (visible: boolean) => {
        if (visible) {
            this.input._previous.world_visible = false
            return
        }
        // Nothing should be typing into a field that is off screen, and on a phone a focused input
        // keeps the soft keyboard up over the game.
        this.input.blur()
        this.input._setDOMInputVisible(false)
    }

    /**
     * Detach the DOM input. pixi-text-input appends it to document.body on 'added' and removes it on
     * 'removed', so the node outlives the canvas unless the field is taken off the stage.
     */
    dispose = () => {
        const dom: HTMLInputElement = this.input._dom_input
        try {
            // The library's 'removed' handler unconditionally does document.body.removeChild, which
            // throws for a field that was disposed before it ever rendered a frame.
            this.input.parent?.removeChild(this.input)
        } catch (e) {
            /* ignore -- the node is detached below either way */
        }
        if (dom && dom.parentNode) {
            dom.parentNode.removeChild(dom)
        }
        this.input._dom_added = false
    }
}
